import * as THREE from "three";

export interface CosmeticPaint {
  kind: string;
  colour: string | null;
  id: string;
}

/** Paint only authored body panels. Glass, lamps, tires, weapons and vegetation retain their finish. */
export function cosmeticPaintWeight(name: string, kind: string): number {
  if (kind === "unit_skin") {
    if (/\.body$/.test(name)) return 1;
    if (/\.(trim|accent)$/.test(name)) return 0.7;
  }
  if (kind === "building_skin") {
    if (name.startsWith("colony.roof.")) return 1;
    if (name.startsWith("colony.accent.")) return 0.8;
    if (name === "colony.porcelain" || name === "colony.ivory") return 0.2;
  }
  return 0;
}

/** Instances share loader materials; every changed material is owned here and restored on disposal. */
export function applyCosmeticPaint(root: THREE.Object3D, cosmetic: CosmeticPaint): () => void {
  if (!cosmetic.colour || !/^#[\da-f]{6}$/i.test(cosmetic.colour)) return () => undefined;
  const colour = new THREE.Color(cosmetic.colour);
  const clones = new Map<THREE.Material, THREE.Material>();
  const originals: { mesh: THREE.Mesh; material: THREE.Material | THREE.Material[] }[] = [];
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const source = Array.isArray(object.material) ? object.material : [object.material];
    let changed = false;
    const painted = source.map((material) => {
      const weight = cosmeticPaintWeight(material.name, cosmetic.kind);
      if (!weight || !(material instanceof THREE.MeshStandardMaterial)) return material;
      changed = true;
      const known = clones.get(material);
      if (known) return known;
      const clone = material.clone();
      clone.color.lerp(colour, weight);
      if (weight >= 0.7 && /gold|chrome/.test(cosmetic.id)) {
        clone.metalness = 0.72;
        clone.roughness = 0.3;
      }
      clones.set(material, clone);
      return clone;
    });
    if (!changed) return;
    originals.push({ mesh: object, material: object.material });
    object.material = Array.isArray(object.material) ? painted : painted[0]!;
  });
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    for (const { mesh, material } of originals) mesh.material = material;
    for (const material of clones.values()) material.dispose();
  };
}
